import { TFunction } from "i18next";

import { showGlobalToast } from "@/utils/showGlobalToast";

import {
	BlacklistOverrideRefusal,
	BlacklistOverrideSkip,
	BlacklistOverrideSkipReason,
	BlacklistOverrideVerdict,
} from "@/pages/Private/pages/Blacklist/schema/blacklist-override";

/**
 * The decisions behind the blacklist override modal (PREC-2548), kept apart from the modal and the
 * hook so they can be read — and tested — on their own. The API decides per lead what may be
 * removed (`classifyBlacklistOverride`); everything here only sorts its answer for the screen.
 */

/**
 * What the modal says the action is: "contact" (assign, compose, reply — the gate takes the leads
 * off the blacklist itself) or "remove" (a status move whose own request takes them off).
 */
export type BlacklistOverrideWording = "contact" | "remove";

/** The modal's title, intro and confirm button per wording — one closed map, so a spec can loop it. */
export const WORDING_KEYS: Record<
	BlacklistOverrideWording,
	{ title: string; intro: string; confirm: string }
> = {
	contact: {
		title: "blacklist.override.title.contact",
		intro: "blacklist.override.intro.contact",
		confirm: "blacklist.override.confirm.contact",
	},
	remove: {
		title: "blacklist.override.title.remove",
		intro: "blacklist.override.intro.remove",
		confirm: "blacklist.override.confirm.remove",
	},
};

/** The verdicts sorted the way the modal shows them. */
export interface BlacklistOverridePlan {
	/** Leads the override can take off the blacklist. */
	overridable: BlacklistOverrideVerdict[];
	/** Leads that stay on it whatever the user decides (an unsubscribe, a Signalist-wide entry). */
	refused: BlacklistOverrideVerdict[];
	/**
	 * A company-wide entry blocks at least one overridable lead. Without removing it the lead is
	 * blocked again at send, so the user has to agree to remove the company before confirming.
	 */
	needsCompanyConsent: boolean;
	/** The companies the "Also remove … from the blacklist" checkbox names. */
	companiesToRemove: string[];
	/** Overridable leads at a competitor; the company stays one and only the person is exempted. */
	competitorCount: number;
	/** The competitor companies the note names. */
	competitorCompanies: string[];
}

const distinctCompanyNames = (verdicts: BlacklistOverrideVerdict[]): string[] => [
	...new Set(
		verdicts.map((verdict) => verdict.companyName?.trim()).filter((name): name is string => !!name)
	),
];

/** Sorts the API's verdicts into what the modal lists, asks and notes. */
export const planBlacklistOverride = (
	verdicts: BlacklistOverrideVerdict[]
): BlacklistOverridePlan => {
	const overridable = verdicts.filter((verdict) => !verdict.refusal);
	const atBlockedCompany = overridable.filter((verdict) => verdict.companyEntryIds.length > 0);
	const atCompetitor = overridable.filter((verdict) => verdict.competitorEntryIds.length > 0);

	return {
		overridable,
		refused: verdicts.filter((verdict) => !!verdict.refusal),
		needsCompanyConsent: atBlockedCompany.length > 0,
		companiesToRemove: distinctCompanyNames(atBlockedCompany),
		competitorCount: atCompetitor.length,
		competitorCompanies: distinctCompanyNames(atCompetitor),
	};
};

/**
 * Whether the confirm button may run: something can be taken off the blacklist, and where a whole
 * company blocks a lead the user agreed to remove it (otherwise the send is blocked again).
 */
export const canConfirmOverride = (plan: BlacklistOverridePlan, removeCompany: boolean): boolean =>
	plan.overridable.length > 0 && (!plan.needsCompanyConsent || removeCompany);

/**
 * The leads an action may go on with once the override ran: every lead that was never
 * blacklisted, and every blacklisted one the override cleared. A refused or skipped lead is never
 * among them, and neither is a blacklisted lead the API did not answer for.
 */
export const leadsAllowedAfterOverride = <T extends { id: number }>(
	leads: T[],
	blacklistedIds: number[],
	clearedIds: number[]
): T[] => leads.filter((lead) => !blacklistedIds.includes(lead.id) || clearedIds.includes(lead.id));

/** Closed maps from the API's reason codes to their text: a code is never shown as a word. */
export const REFUSAL_LABEL_KEYS: Record<BlacklistOverrideRefusal, string> = {
	UNSUBSCRIBED: "blacklist.override.refusal.unsubscribed",
	GLOBAL: "blacklist.override.refusal.global",
};

export const SKIP_REASON_LABEL_KEYS: Record<BlacklistOverrideSkipReason, string> = {
	UNSUBSCRIBED: "blacklist.override.skipReason.unsubscribed",
	GLOBAL: "blacklist.override.skipReason.global",
	COMPANY_NOT_REMOVED: "blacklist.override.skipReason.companyNotRemoved",
};

/** The company list for a sentence, or "their company" when the API knows no name. */
export const formatCompanyNames = (names: string[], t: TFunction): string =>
	names.length > 0 ? names.join(", ") : t("blacklist.override.unknownCompany");

/**
 * The toast for the leads an override left on the blacklist — "Kept on the blacklist: Ada
 * (unsubscribed), Ben (Signalist-wide entry)" — or null when it left none.
 */
export const describeSkippedLeads = (
	skipped: Pick<BlacklistOverrideSkip, "name" | "reason">[] | null | undefined,
	t: TFunction
): string | null => {
	if (!skipped || skipped.length === 0) {
		return null;
	}

	// A reason this build does not know is left out rather than shown as its code (web rule §10).
	const names = skipped
		.map((lead) => {
			const reasonKey = SKIP_REASON_LABEL_KEYS[lead.reason];

			return reasonKey ? `${lead.name} (${t(reasonKey)})` : lead.name;
		})
		.join(", ");

	return t("blacklist.override.skippedToast", { names });
};

/**
 * Names the leads an override left on the blacklist in a toast, so a lead that silently stayed
 * behind is never read as one that was contacted. Does nothing when it left none.
 */
export const showSkippedLeadsToast = (
	skipped: Pick<BlacklistOverrideSkip, "name" | "reason">[] | null | undefined,
	t: TFunction
): void => {
	const message = describeSkippedLeads(skipped, t);

	if (message) {
		showGlobalToast(message, 8000);
	}
};
